import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Receipt,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { bandejaKeys } from "@/lib/queryKeys";
import { getAccountantInbox } from "@/services/dashboardService";
import {
  BandejaRow,
  BandejaRowList,
} from "@/components/features/accountant/BandejaRow";
import { BANDEJA_SECTIONS } from "@/components/features/accountant/BandejaRowMeta";

const SECTION_PREVIEW = 10;

/**
 * Bandeja landing — three sections side-by-side, top 10 each, each
 * with a deep-link to its full page. Designed as the default work
 * surface for accountants who think task-first ("what do I need to do
 * this morning?") rather than client-first.
 */
export function BandejaPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const { data, isLoading } = useQuery({
    queryKey: bandejaKeys.composite(),
    queryFn: getAccountantInbox,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  if (isLoading || !data) return <LoadingPage />;

  const allClear =
    data.pendingAcks.count === 0 &&
    data.pendingBoletos.count === 0 &&
    data.upcomingObligations.count === 0;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("bandeja.page.eyebrow", "Tu cartera, una sola lista")}
        title={t("bandeja.page.title", "Bandeja")}
        description={t(
          "bandeja.page.description",
          "Todas las tareas pendientes de tus clientes en un solo lugar.",
        )}
        onBack={() => navigate("/accountant")}
      />

      {allClear ? (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <Card className="border-[var(--fx-accent-300)]/40 bg-[var(--fx-accent-100)]/30">
            <CardContent className="flex items-center gap-4 py-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--fx-accent-100)]">
                <CheckCircle2
                  className="h-6 w-6 text-[var(--fx-accent-500)]"
                  aria-hidden
                />
              </div>
              <div>
                <div className="text-lg font-semibold text-foreground">
                  {t("bandeja.page.allClearTitle", "Bandeja al día")}
                </div>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "bandeja.page.allClearDescription",
                    "No hay tareas pendientes en tu cartera. Volvé luego para chequear nuevamente.",
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <div className="space-y-4">
          {data.pendingAcks.count > 0 ? (
            <BandejaPageSection
              icon={<Receipt className="h-4 w-4" aria-hidden />}
              title={BANDEJA_SECTIONS.acks.title}
              description={BANDEJA_SECTIONS.acks.description}
              count={data.pendingAcks.count}
              overdueCount={data.pendingAcks.overdueCount}
              route={BANDEJA_SECTIONS.acks.route}
            >
              <BandejaRowList>
                {data.pendingAcks.items.slice(0, SECTION_PREVIEW).map((item) => (
                  <BandejaRow key={item.recibidaId} kind="ack" item={item} />
                ))}
              </BandejaRowList>
            </BandejaPageSection>
          ) : null}

          {data.pendingBoletos.count > 0 ? (
            <BandejaPageSection
              icon={<FileText className="h-4 w-4" aria-hidden />}
              title={BANDEJA_SECTIONS.boletos.title}
              description={BANDEJA_SECTIONS.boletos.description}
              count={data.pendingBoletos.count}
              overdueCount={data.pendingBoletos.overdueCount}
              route={BANDEJA_SECTIONS.boletos.route}
            >
              <BandejaRowList>
                {data.pendingBoletos.items
                  .slice(0, SECTION_PREVIEW)
                  .map((item) => (
                    <BandejaRow
                      key={item.obligationId}
                      kind="boleto"
                      item={item}
                    />
                  ))}
              </BandejaRowList>
            </BandejaPageSection>
          ) : null}

          {data.upcomingObligations.count > 0 ? (
            <BandejaPageSection
              icon={<FileText className="h-4 w-4" aria-hidden />}
              title={BANDEJA_SECTIONS.obligations.title}
              description={BANDEJA_SECTIONS.obligations.description}
              count={data.upcomingObligations.count}
              overdueCount={data.upcomingObligations.overdueCount}
              route={BANDEJA_SECTIONS.obligations.route}
            >
              <BandejaRowList>
                {data.upcomingObligations.items
                  .slice(0, SECTION_PREVIEW)
                  .map((item) => (
                    <BandejaRow
                      key={item.obligationId}
                      kind="obligation"
                      item={item}
                    />
                  ))}
              </BandejaRowList>
            </BandejaPageSection>
          ) : null}
        </div>
      )}
    </div>
  );
}

function BandejaPageSection({
  icon,
  title,
  description,
  count,
  overdueCount,
  route,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  count: number;
  overdueCount: number;
  route: string;
  children: React.ReactNode;
}): JSX.Element {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
    >
      <Card>
        <div className="flex items-start justify-between gap-3 border-b border-[var(--fx-ink-100)] bg-[var(--fx-paper-2)]/40 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--fx-ink-100)] text-[var(--fx-ink-700)]">
              {icon}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">
                  {title}
                </span>
                <span className="inline-flex items-center rounded-full bg-[var(--fx-ink-100)] px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-[var(--fx-ink-700)]">
                  {count}
                </span>
                {overdueCount > 0 ? (
                  <span className="inline-flex items-center rounded-full bg-[var(--fx-warn)]/10 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-[var(--fx-warn-text)]">
                    {overdueCount} vencidos
                  </span>
                ) : null}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {description}
              </p>
            </div>
          </div>
          {count > SECTION_PREVIEW ? (
            <Button asChild variant="ghost" size="sm" className="h-8 gap-1.5">
              <Link to={route}>
                Ver todos
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </Button>
          ) : null}
        </div>
        <div className="space-y-1 px-3 py-2">{children}</div>
        {count > SECTION_PREVIEW ? (
          <div className="border-t border-[var(--fx-ink-100)] px-4 py-2 text-right">
            <Button asChild variant="link" size="sm" className="h-7 text-xs">
              <Link to={route}>
                {count - SECTION_PREVIEW > 0
                  ? `Hay ${count - SECTION_PREVIEW} más`
                  : "Ver todos"}
              </Link>
            </Button>
          </div>
        ) : null}
      </Card>
    </motion.div>
  );
}
